// SidebarGroup — one row in the sidebar's bottom block. The row is a link that navigates to the
// group's overview route and highlights when the current route is that overview or one of its
// children.
//
// There is no expand/collapse chevron: the bottom block is a flat list of destinations. Sub-routes
// are reached from each destination's own in-page navigation (Settings has its tab bar, Organization
// has the OrgSettingsLayout subnav), so a rail-level accordion was redundant. `items` is still
// accepted because it drives the active-trail highlight — a child route lights its parent row.
//
// The Phase 22 collapsed-icon-rail flyout branch is gone — the rail no longer collapses (AppLayout
// force-sets sidebarCollapsed=false). WorkflowSwitcher owns the one remaining fixed-flyout case via
// useFlyoutPosition.
import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';

export interface RouteMatch {
  pathname: string;
  search: string;
}

export interface SidebarGroupChild {
  to: string;
  label: string;
  isActive: (loc: RouteMatch) => boolean;
  badge?: ReactNode;
}

export interface SidebarGroupProps {
  icon: LucideIcon;
  label: string;
  overviewTo: string;
  isOverviewActive: (loc: RouteMatch) => boolean;
  items: SidebarGroupChild[];
  badge?: ReactNode;
  onNavigate?: () => void;
}

export default function SidebarGroup({
  icon: Icon,
  label,
  overviewTo,
  isOverviewActive,
  items,
  badge,
  onNavigate,
}: SidebarGroupProps) {
  const location = useLocation();
  const loc: RouteMatch = { pathname: location.pathname, search: location.search };

  // A child route lights the parent row, so navigating into e.g. /settings/appearance still shows
  // where you are even though the children are not listed in the rail.
  const activeTrail = isOverviewActive(loc) || items.some((c) => c.isActive(loc));

  return (
    <Link
      to={overviewTo}
      className={`sidebar-nav-link sidebar-nav-link--muted sidebar-group-label${activeTrail ? ' active' : ''}`}
      onClick={onNavigate}
      aria-label={label}
      aria-current={activeTrail ? 'page' : undefined}
    >
      <Icon className="sidebar-icon" aria-hidden="true" />
      <span className="sidebar-nav-label">{label}</span>
      {badge}
    </Link>
  );
}
